"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Label } from "../ui/label"
import { Switch } from "../ui/switch"
import { Save, X } from 'lucide-react'
import { apiClient } from "../lib/apiClient"
import { useToast } from "../hooks/use-toast"

interface SystemUserFormData {
  userID: number
  isActive: boolean
  employeeID: string
  locationID: number | null
  departmentID: number | null
  newRoles: string
  joiningDate: string
  displayName: string
  email: string
}

interface Location {
  id: number
  name: string
}

interface Department {
  id: number
  name: string
}

interface Role {
  id: number
  name: string
}

interface AddEditSystemUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialData?: SystemUserFormData | null
  locations?: Location[]
  departments?: Department[]
}

export function AddEditSystemUserModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  initialData,
  locations = [],
  departments = []
}: AddEditSystemUserModalProps) {
  const { toast } = useToast()
  const [formData, setFormData] = useState<SystemUserFormData>({
    userID: 0,
    isActive: true,
    employeeID: "",
    locationID: null,
    departmentID: null,
    newRoles: "",
    joiningDate: "",
    displayName: "",
    email: "",
  })

  const [roles, setRoles] = useState<Role[]>([])
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]) // Store role names (keys) for display
  const [loadingRoles, setLoadingRoles] = useState(false)

  const [errors, setErrors] = useState<{
    newRoles?: string
    joiningDate?: string
    displayName?: string
    email?: string
  }>({})

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch lookup values (roles, departments, locations) when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchLookupValues()
    }
  }, [isOpen])

  const fetchLookupValues = async () => {
    try {
      setLoadingRoles(true)
      // Use the Helper/GetLookupValues endpoint with the GUID
      // The GUID appears to be: 3228D5A1-DA07-45AC-8F99-CDE6C239EC6B,739F2F0D-76F0-4FB9-A778-B97AE5173629,E771BF28-E6E9-4263-B9F4-29D80B38C8E0
      const guid = "3228D5A1-DA07-45AC-8F99-CDE6C239EC6B%2C739F2F0D-76F0-4FB9-A778-B97AE5173629%2CE771BF28-E6E9-4263-B9F4-29D80B38C8E0"
      const response = await apiClient.get(`/Helper/GetLookupValues/${guid}`)
      
      console.log("Lookup values response:", response.data)
      
      if (Array.isArray(response.data)) {
        // Filter by categoryName
        const rolesData = response.data
          .filter((item: any) => item.categoryName === 'Roles')
          .map((item: any) => ({ 
            id: item.value,  // value is the ID
            name: item.key   // key is the display name
          }))
        
        setRoles(rolesData)
        console.log("Parsed roles:", rolesData)
      } else {
        setRoles([])
      }
    } catch (err: any) {
      console.error("Error fetching lookup values:", err)
      setRoles([])
    } finally {
      setLoadingRoles(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      console.log("Modal opened with locations:", locations)
      console.log("Modal opened with departments:", departments)
      console.log("Modal opened with initialData:", initialData)
      
      if (initialData) {
        // initialData.newRoles contains role names (keys) as comma-separated string
        let rolesArray: string[] = []
        if (initialData.newRoles) {
          rolesArray = initialData.newRoles.split(',').map(r => r.trim()).filter(r => r)
        }
        setSelectedRoles(rolesArray)
        
        // Find location and department IDs by matching names from the lookup data
        // Note: We need to wait for locations/departments to be loaded, so this will be handled in a separate effect
        setFormData({
          userID: initialData.userID || 0,
          isActive: initialData.isActive ?? true,
          employeeID: initialData.employeeID || "",
          locationID: initialData.locationID || null,
          departmentID: initialData.departmentID || null,
          newRoles: initialData.newRoles || "",
          joiningDate: initialData.joiningDate ? initialData.joiningDate.split('T')[0] : "",
          displayName: initialData.displayName || "",
          email: initialData.email || "",
        })
      } else {
        // Reset form for new entry
        setSelectedRoles([])
        setFormData({
          userID: 0,
          isActive: true,
          employeeID: "",
          locationID: null,
          departmentID: null,
          newRoles: "",
          joiningDate: "",
          displayName: "",
          email: "",
        })
      }
      setErrors({})
    }
  }, [isOpen, initialData, locations, departments])

  const validate = (): boolean => {
    const newErrors: typeof errors = {}
    
    // Location and Department are now optional - no validation needed
    
    if (selectedRoles.length === 0) {
      newErrors.newRoles = "At least one role is required"
    }
    
    if (!formData.joiningDate || formData.joiningDate.trim() === "") {
      newErrors.joiningDate = "Joining date is required"
    }
    
    if (!formData.displayName || formData.displayName.trim() === "") {
      newErrors.displayName = "Display name is required"
    }
    
    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = "Email is required"
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address"
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isFormValid = (): boolean => {
    return (
      selectedRoles.length > 0 &&
      formData.joiningDate.trim() !== "" &&
      formData.displayName.trim() !== "" &&
      formData.email.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    )
  }

  const handleSave = async () => {
    if (!validate()) {
      return
    }

    setIsSubmitting(true)
    try {
      // Convert selected role names (keys) to role IDs (values) and join with commas
      // This matches the .NET code behavior: convert role keys to role IDs
      const roleIds = selectedRoles
        .map(roleName => {
          const role = roles.find(r => r.name === roleName)
          return role ? role.id : null
        })
        .filter(id => id !== null)
        .map(id => id.toString())
        .join(',')
      
      // Ensure roleIds is not empty
      if (!roleIds || roleIds.trim() === '') {
        toast({
          title: "Error",
          description: "Please select at least one role",
          duration: 4000,
          className: "border border-red-300",
        })
        setIsSubmitting(false)
        return
      }
      
      // Get current user ID from localStorage (set during login)
      const currentUserId = localStorage.getItem("id") || sessionStorage.getItem("id")
      
      // Match the C# DTO structure (PascalCase)
      // The backend expects NewRoles, but validation error mentions "Roles" field
      // So we'll send both to ensure compatibility
      const payload: any = {
        UserID: formData.userID,
        IsActive: formData.isActive,
        EmployeeID: formData.employeeID || "",
        LocationID: formData.locationID || 0,
        DepartmentID: formData.departmentID || 0,
        NewRoles: roleIds, // Primary field name from DTO
        JoiningDate: formData.joiningDate ? new Date(formData.joiningDate).toISOString() : null,
        DisplayName: formData.displayName,
        Email: formData.email,
      }
      
      // Only include ModUser if we have a user ID (backend might set it from session, but include it for safety)
      if (currentUserId) {
        payload.ModUser = parseInt(currentUserId)
      }
      
      // Remove Roles field - only send NewRoles to avoid confusion
      // The backend validation error mentioned "Roles" but the DTO uses "NewRoles"
      
      console.log("Saving payload:", payload)
      console.log("Is Add operation:", formData.userID === 0)
      console.log("Role IDs being sent:", roleIds)
      console.log("Selected roles:", selectedRoles)
      console.log("Available roles:", roles)
      console.log("Current user ID:", currentUserId)

      const response = await apiClient.post("/admin/UpdateSystemUser", payload)
      
      const isAdd = formData.userID === 0
      // Handle both response.data.Result and direct response.data
      const resultMessage = response.data?.Result || response.data?.result || response.data
      const message = typeof resultMessage === 'string' 
        ? resultMessage 
        : (isAdd ? "User added successfully" : "User updated successfully")
      
      toast({
        title: "Success",
        description: message,
        duration: 3000,
        className: "border border-green-300",
      })

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error("Error saving system user:", error)
      console.error("Error response:", error?.response?.data)
      
      // Extract validation errors if available
      const errorMessage = error?.response?.data?.errors 
        ? Object.values(error.response.data.errors).flat().join(', ')
        : error?.response?.data?.message || error?.message || "Failed to save system user"
      
      toast({
        title: "Error",
        description: errorMessage,
        duration: 4000,
        className: "border border-red-300",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-white border border-gray-200 rounded-lg shadow-lg p-0 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6 flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-bold text-gray-900">
            {initialData ? "Edit System User" : "Add New System User"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-6 px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-sm font-medium text-gray-700">
                Display Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="displayName"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter display name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeID" className="text-sm font-medium text-gray-700">
                Employee ID
              </Label>
              <Input
                id="employeeID"
                value={formData.employeeID}
                onChange={(e) => setFormData({ ...formData, employeeID: e.target.value })}
                className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter employee ID (optional)"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="joiningDate" className="text-sm font-medium text-gray-700">
                Joining Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="joiningDate"
                type="date"
                value={formData.joiningDate}
                onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })}
                className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" className="text-sm font-medium text-gray-700">
                Location
              </Label>
              <Select
                value={formData.locationID ? formData.locationID.toString() : "none"}
                onValueChange={(value) => {
                  setFormData({ ...formData, locationID: value === "none" ? null : parseInt(value) })
                }}
              >
                <SelectTrigger className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500 w-full">
                  <SelectValue placeholder={locations.length === 0 ? "No locations available" : "Select Location (Optional)"} />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 max-h-[200px]">
                  <SelectItem value="none" className="text-gray-900 hover:bg-gray-50">
                    -- None --
                  </SelectItem>
                  {locations.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">No locations available</div>
                  ) : (
                    locations.map((location) => (
                      <SelectItem key={location.id} value={location.id.toString()} className="text-gray-900 hover:bg-gray-50">
                        {location.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="department" className="text-sm font-medium text-gray-700">
                Department
              </Label>
              <Select
                value={formData.departmentID ? formData.departmentID.toString() : "none"}
                onValueChange={(value) => {
                  setFormData({ ...formData, departmentID: value === "none" ? null : parseInt(value) })
                }}
              >
                <SelectTrigger className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500 w-full">
                  <SelectValue placeholder={departments.length === 0 ? "No departments available" : "Select Department (Optional)"} />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 max-h-[200px]">
                  <SelectItem value="none" className="text-gray-900 hover:bg-gray-50">
                    -- None --
                  </SelectItem>
                  {departments.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">No departments available</div>
                  ) : (
                    departments.map((department) => (
                      <SelectItem key={department.id} value={department.id.toString()} className="text-gray-900 hover:bg-gray-50">
                        {department.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="newRoles" className="text-sm font-medium text-gray-700">
                Roles <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-white min-h-[42px]">
                {loadingRoles ? (
                  <span className="text-sm text-gray-500">Loading roles...</span>
                ) : roles.length === 0 ? (
                  <span className="text-sm text-gray-500">No roles available</span>
                ) : (
                  roles.map((role) => {
                    // Use role name (key) for selection, not ID
                    const isSelected = selectedRoles.includes(role.name)
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => {
                          const newSelected = isSelected
                            ? selectedRoles.filter(name => name !== role.name)
                            : [...selectedRoles, role.name]
                          setSelectedRoles(newSelected)
                          // Store role names for display, but we'll convert to IDs when saving
                          setFormData({ ...formData, newRoles: newSelected.join(', ') })
                        }}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          isSelected
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {role.name}
                      </button>
                    )
                  })
                )}
              </div>
              {selectedRoles.length === 0 && !loadingRoles && (
                <p className="text-xs text-gray-500">Select one or more roles</p>
              )}
            </div>

            <div className="space-y-2 flex items-center">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Active
                </Label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="px-6 py-2.5 text-base font-medium rounded-md"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!isFormValid() || isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white px-6 py-2.5 text-base font-medium rounded-md shadow-sm"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

